import React, { useState, useEffect, useRef } from 'react';
import type { z } from 'zod';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';
import { Button, Chip, Input } from '../../components/ui';
import { FieldChangeIndicator } from '../../components/ui/FieldChangeIndicator.js';
import type { FieldChange } from '../../hooks/useFieldChangeTracker.js';
import { AlertTriangle, Search, Sparkles, X } from 'lucide-react';
import {
  Form,
  FormFullWidth,
  FormSection,
  NumberFormField,
  SelectFormField,
  SwitchFormField,
  TextFormField,
  type ChoiceOption,
} from '../../components/form';
import { zWorkflowStepInput } from '../../generated/api/zod.gen.js';
import { useBuilderForm } from './useBuilderForm.js';
import { agentApi } from '../../services/api';
import { StepTemplateMetadata } from './StepTemplateMetadata.js';
import { SafeOutputsEditor } from './SafeOutputsEditor.js';
import type { AgentTemplateSummary, AgentTemplateSearchResult } from '../../generated/api/types.gen.js';

interface StepConfigTabProps {
  step: WorkflowBuilderStep;
  steps: WorkflowBuilderStep[];
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  roles: AgentTemplateSummary[];
  changes?: Map<string, FieldChange>;
}

const labelClass = 'block text-sm font-medium text-input-label-text mb-1.5';

const MODE_OPTIONS: ChoiceOption[] = [
  { value: 'sequential', label: 'Sequential' },
  { value: 'parallel', label: 'Parallel' },
  { value: 'conditional', label: 'Conditional' },
  { value: 'race', label: 'Race' },
];

/** The step fields this tab edits, from the generated step schema. */
const stepFieldsSchema = zWorkflowStepInput.pick({
  name: true,
  description: true,
  executionMode: true,
  parallelGroup: true,
  order: true,
  optional: true,
});
type StepFields = z.input<typeof stepFieldsSchema>;

type TemplateSearchResult = AgentTemplateSearchResult;

export const StepConfigTab: React.FC<StepConfigTabProps> = ({ step, onUpdateStep, roles, changes }) => {
  const p = `steps.${step.id.toString()}`;
  const stepId = step.id.toString();

  const fields = useBuilderForm(
    stepFieldsSchema,
    {
      name: step.name || '',
      description: step.description ?? '',
      executionMode: step.executionMode ?? 'sequential',
      parallelGroup: step.parallelGroup ?? '',
      order: step.order,
      optional: step.optional,
    },
    (field, all) => {
      switch (field) {
        case 'name':
          return onUpdateStep(stepId, { name: all.name ?? '' });
        case 'description':
          return onUpdateStep(stepId, { description: all.description ?? '' });
        case 'executionMode':
          return onUpdateStep(stepId, { executionMode: all.executionMode ?? 'sequential' });
        case 'parallelGroup':
          return onUpdateStep(stepId, { parallelGroup: all.parallelGroup || null });
        case 'order':
          return all.order === undefined ? undefined : onUpdateStep(stepId, { order: all.order });
        case 'optional':
          return onUpdateStep(stepId, { optional: all.optional ?? false });
      }
    },
  );

  // Template search state
  const [templateSearch, setTemplateSearch] = useState('');
  const [searchResults, setSearchResults] = useState<TemplateSearchResult[] | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [recommendation, setRecommendation] = useState<TemplateSearchResult | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Find the currently selected role for display
  const currentRole = roles.find((r) => r.id === step.templateId || r.slug === step.templateSlug);
  const currentRoleDisabled =
    step.templateSlug && roles.some((r) => (r.id === step.templateId || r.slug === step.templateSlug) && r.disabled);

  // Debounced template search
  useEffect(() => {
    if (!templateSearch.trim()) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(() => {
      agentApi
        .searchTemplates(templateSearch, 10)
        .then((data) => {
          // QG-CLI-PAGES-STEPCFG: the response nests results under `data.data` (see
          // SearchAgentTemplatesResponses in types.gen.ts) — this read `data.results`, a field
          // that never existed, so `data.results ?? []` was always `[]` and the template search
          // dropdown never showed a result no matter what was typed.
          setSearchResults(data.data ?? []);
          setSearchOpen(true);
        })
        .catch(() => setSearchResults(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [templateSearch]);

  // AI recommendation when step description changes
  useEffect(() => {
    if (!step.description || step.description.length < 10) {
      setRecommendation(null);
      return;
    }
    const timer = setTimeout(() => {
      agentApi
        .recommendTemplates(step.description ?? '')
        .then((data) => {
          // Same nesting fix as searchTemplates above — the recommendation list lives at `data.data`.
          const results = data.data ?? [];
          if (results.length > 0 && results[0].slug !== step.templateSlug) {
            setRecommendation(results[0]);
          } else {
            setRecommendation(null);
          }
        })
        .catch(() => setRecommendation(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [step.description, step.templateSlug]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectTemplate = (result: TemplateSearchResult) => {
    onUpdateStep(step.id.toString(), {
      templateId: result.id,
      templateSlug: result.slug,
    });
    setTemplateSearch('');
    setSearchResults(null);
    setSearchOpen(false);
  };

  const clearSelection = () => {
    onUpdateStep(step.id.toString(), {
      templateId: null,
      templateSlug: null,
    });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <Form form={fields} onSubmit={() => undefined} className="gap-3">
        <FormSection title="Step">
          <div className="relative">
            <TextFormField<StepFields> name="name" label="Name" />
            <FieldChangeIndicator fieldPath={`${p}.name`} changes={changes} />
          </div>
          <div className="relative" ref={searchRef}>
            <label className={labelClass}>Agent template</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-3 text-faint" />
              <Input
                type="text"
                aria-label="Search agent templates"
                placeholder="Search templates..."
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults) setSearchOpen(true);
                }}
                className={`pl-7 pr-9 ${step.templateSlug && currentRoleDisabled ? 'border-warning' : ''}`}
              />
              {templateSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Clear search"
                  className="absolute right-1 top-0.5 size-7"
                  onClick={() => {
                    setTemplateSearch('');
                    setSearchResults(null);
                    setSearchOpen(false);
                  }}
                >
                  <X size={14} />
                </Button>
              )}
            </div>

            {/* Current selection */}
            {step.templateSlug && (
              <div className="mt-1 flex items-center gap-1.5 text-xs">
                <span className="text-muted">Selected:</span>
                <span className="text-heading font-medium">{currentRole?.title ?? step.templateSlug}</span>
                {currentRole?.departmentId && <span className="text-faint">({currentRole.departmentId})</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Clear selection"
                  title="Clear selection"
                  className="size-6"
                  onClick={clearSelection}
                >
                  <X size={12} />
                </Button>
              </div>
            )}
            <StepTemplateMetadata step={step} template={currentRole} />

            {currentRoleDisabled && (
              <div className="flex items-center gap-1 mt-1 text-warning text-xs">
                <AlertTriangle size={12} />
                <span>This template is disabled. The workflow cannot be activated until this is resolved.</span>
              </div>
            )}

            {/* AI Recommendation */}
            {recommendation && (
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => selectTemplate(recommendation)}>
                <Sparkles size={12} />
                <span>Suggested: {recommendation.name}</span>
                {recommendation.departmentId && <span className="text-faint">({recommendation.departmentId})</span>}
              </Button>
            )}

            {/* Search results dropdown */}
            {searchOpen && searchResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-surface border rounded-lg shadow-lg max-h-48 overflow-auto">
                {searchResults.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => selectTemplate(r)}
                    className={`px-3 py-2 hover:bg-surface-hover cursor-pointer text-sm border-b border-border last:border-b-0 ${
                      step.templateSlug === r.slug ? 'bg-surface-alt' : ''
                    }`}
                  >
                    <div className="text-heading font-medium">{r.name}</div>
                    <div className="flex items-center gap-2 text-xs text-muted mt-0.5">
                      {r.departmentId && <span>{r.departmentId}</span>}
                      {r.platform && <Chip>{r.platform}</Chip>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searchOpen && searchResults && searchResults.length === 0 && templateSearch.trim() && (
              <div className="absolute z-10 w-full mt-1 bg-surface border rounded-lg shadow-lg p-3 text-sm text-muted">
                No templates found for "{templateSearch}"
              </div>
            )}
            <FieldChangeIndicator fieldPath={`${p}.templateSlug`} changes={changes} />
          </div>
          <FormFullWidth>
            <div className="relative">
              <TextFormField<StepFields> name="description" label="Description" />
              <FieldChangeIndicator fieldPath={`${p}.description`} changes={changes} />
            </div>
          </FormFullWidth>
          <div className="relative">
            <SelectFormField<StepFields> name="executionMode" label="Mode" options={MODE_OPTIONS} />
            <FieldChangeIndicator fieldPath={`${p}.executionMode`} changes={changes} />
          </div>
          {step.executionMode === 'parallel' && (
            <TextFormField<StepFields> name="parallelGroup" label="Parallel group" placeholder="group-name" />
          )}
          <div className="relative">
            <NumberFormField<StepFields>
              name="order"
              label="Execution order (tier)"
              description="Steps at the same order run in the same tier"
              minValue={1}
            />
          </div>
          <div className="relative flex items-center gap-2">
            <SwitchFormField<StepFields> name="optional" label="Optional (continue on failure)" />
            <FieldChangeIndicator fieldPath={`${p}.optional`} changes={changes} />
          </div>
        </FormSection>
      </Form>
      <details className="shrink-0">
        <summary className="text-xs font-medium text-muted cursor-pointer select-none">
          Safe outputs: per-tool guardrails
        </summary>
        <div className="mt-2">
          <SafeOutputsEditor
            key={step.id.toString()}
            value={step.safeOutputs}
            onChange={(safeOutputs) => onUpdateStep(step.id.toString(), { safeOutputs })}
          />
        </div>
      </details>
    </div>
  );
};
